import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { HandCoins, TrendingUp, Gift, CalendarDays, BellRing } from "lucide-react";
import { PageHeader, StatCard } from "@/components/PageHeader";
import { NativeSelect } from "@/components/Field";
import { PageBlocks } from "@/components/content/BlockView";
import { GoalCards } from "@/components/GoalCards";
import { useApp } from "@/context/AppContext";
import { api } from "@/lib/api";
import { buildSummary, progressStats } from "@/lib/reports";
import { money, fmtDate, thisYear, yearOptions, slug } from "@/lib/format";

const ACTIONS = [
  { to: "/tithes?new=1", label: "Add Tithe", icon: HandCoins, feature: "tithes" },
  { to: "/progress?new=1", label: "Progress Report", icon: TrendingUp, feature: "progress" },
  { to: "/donations?new=1", label: "Add Conference Collection", icon: Gift, feature: "donations" },
  { to: "/conference", label: "Register for Conference", icon: CalendarDays, feature: "conference" },
];

function useYearData(year) {
  const { isAdmin, featureOn, hasArea } = useApp();
  const [d, setD] = useState({ tithes: [], donations: [], progress: [], regs: [] });
  useEffect(() => {
    const p = { params: { year } };
    const allowed = (f, areas) => (isAdmin ? hasArea(...areas) : featureOn(f));
    const get = (path, f, opts, areas = ["records", "reports"]) => (allowed(f, areas) ? api.get(path, opts).then((r) => r.data).catch(() => []) : Promise.resolve([]));
    Promise.all([get("/tithes", "tithes", p), get("/donations", "donations", p), get("/progress", "progress", p), get("/registrations", "conference", undefined, ["conference"])])
      .then(([tithes, donations, progress, regs]) => setD({ tithes, donations, progress, regs }));
  }, [year, isAdmin, featureOn, hasArea]);
  return d;
}

function ChurchTable({ rows }) {
  return (
    <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
      <table className="w-full text-sm" data-testid="dashboard-church-table">
        <thead><tr className="border-b bg-[hsl(var(--sand))] text-left">
          <th className="px-4 py-3">Church</th><th className="px-4 py-3">State</th><th className="px-4 py-3 text-right">Tithes</th>
          <th className="px-4 py-3 text-right">Conference Collection</th><th className="px-4 py-3">Last progress report</th>
        </tr></thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan={5} className="px-4 py-10 text-center text-slate-500">No churches yet. Add churches in the Admin Panel.</td></tr>}
          {rows.map((r) => (
            <tr key={r.id} className="border-b border-slate-100">
              <td className="px-4 py-3 font-semibold text-slate-900">{r.name}</td><td className="px-4 py-3">{r.state}</td>
              <td className="px-4 py-3 text-right font-mono">{money(r.tithe)}</td><td className="px-4 py-3 text-right font-mono">{money(r.don)}</td>
              <td className="px-4 py-3">{r.stats.last_date ? fmtDate(r.stats.last_date) : <span className="text-amber-700">None yet</span>}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function MissingBanner() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/alerts/missing").then((r) => setD(r.data)).catch(() => {}); }, []);
  if (!d || !d.missing_count) return null;
  const names = d.churches.filter((c) => c.missing).map((c) => c.name);
  return (
    <Link to="/alerts" data-testid="missing-banner" className="flex items-start gap-3 rounded-xl border border-amber-300 bg-amber-50 p-4 text-amber-900 transition-colors hover:bg-amber-100">
      <BellRing className="mt-0.5 h-5 w-5 shrink-0" />
      <span><b data-testid="missing-banner-count">{d.missing_count} church{d.missing_count > 1 ? "es" : ""}</b> still missing this month's MWJK tithe or progress report: {names.slice(0, 5).join(", ")}{names.length > 5 ? ` and ${names.length - 5} more` : ""}. <u>View &amp; send reminders</u></span>
    </Link>
  );
}

export default function Dashboard() {
  const ctx = useApp();
  const [year, setYear] = useState(String(thisYear()));
  const d = useYearData(year);
  const rows = useMemo(() => buildSummary({ churches: ctx.churches, ...d }), [ctx.churches, d]);
  const stats = progressStats(d.progress);
  const destTotals = ctx.options.destinations.filter((o) => o.active).map((o) => ({ name: o.name, total: d.tithes.filter((t) => t.destination === o.name).reduce((a, t) => a + t.amount, 0) }));
  const recs = !ctx.isAdmin || ctx.hasArea("records", "reports");
  const show = (f) => (ctx.isAdmin ? (f === "conference" ? ctx.hasArea("conference") : recs) : ctx.featureOn(f));
  const canAct = (f) => (ctx.isAdmin ? (f === "conference" ? ctx.hasArea("conference") : ctx.hasArea("records")) : ctx.featureOn(f));

  return (
    <div className="space-y-10">
      <PageHeader eyebrow={ctx.isAdmin ? "Master Admin" : ctx.myChurch?.name} title={`Iakwe, ${ctx.user.name.split(" ")[0]}`}
        subtitle={ctx.isAdmin ? `Overview of all ${ctx.churches.length} churches` : "Here is your church summary"}>
        <NativeSelect value={year} onChange={setYear} options={yearOptions()} testid="dashboard-year-select" className="w-36" />
      </PageHeader>
      {ctx.isAdmin && ctx.hasArea("reminders") && <MissingBanner />}
      <PageBlocks page="dashboard" />

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        {ACTIONS.filter((a) => canAct(a.feature)).map((a) => (
          <Link key={a.to} to={a.to} data-testid={`quick-${slug(a.label)}`}
            className="group flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-4 font-semibold text-slate-800 transition-colors hover:border-[hsl(var(--primary))] hover:bg-blue-50/40">
            <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-[hsl(var(--primary))] text-white transition-transform group-hover:scale-105"><a.icon className="h-5 w-5" /></span>
            {a.label}
          </Link>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {show("tithes") && destTotals.map((t, i) => <StatCard key={t.name} label={`${t.name} Tithe`} sub={year} value={money(t.total)} accent={i % 2 ? "gold" : "primary"} testid={`stat-tithe-${slug(t.name)}`} />)}
        {show("donations") && <StatCard label="Conference Collection" sub={year} value={money(d.donations.reduce((a, x) => a + x.amount, 0))} accent="emerald" testid="stat-donations" />}
        {show("conference") && <StatCard label="Conference registrations" sub="All events" value={d.regs.length} accent="coral" testid="stat-registrations" />}
      </div>

      <GoalCards enabled={ctx.feat("goal_cards") && show("donations")} />

      {show("progress") && (
        <section>
          <h2 className="text-on-bg mb-3 font-heading text-xl font-bold">Progress {year}</h2>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <StatCard label="Ri-Jar" sub="Avg. Sunday attendance" value={stats.ri_jar} testid="stat-ri-jar" />
            <StatCard label="Active Members" sub="Latest report" value={stats.active_members} accent="gold" testid="stat-active-members" />
            <StatCard label="Ukwelok" sub="Came to Jesus" value={stats.ukwelok} accent="emerald" testid="stat-ukwelok" />
            <StatCard label="Baptise ilo Dren" sub="Water baptisms" value={stats.baptise_ilo_dren} accent="coral" testid="stat-baptise" />
          </div>
        </section>
      )}

      {ctx.isAdmin && recs && (
        <section>
          <h2 className="text-on-bg mb-3 font-heading text-xl font-bold">Churches — {year}</h2>
          <ChurchTable rows={rows} />
        </section>
      )}
    </div>
  );
}
